import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DeleteIcon, PenIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import { Paths } from '../../../lib/constants/path'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { EndOfJourneyTemplate } from '../../../lib/types/models/end-of-journey-template/end-of-journey-template'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import CountryFlag from '../../elements/country-flag'
import EndOfJourneyTemplateDeleteModal from './end-of-journey-template-delete-modal'

export type Props = {
  template: EndOfJourneyTemplate
  onRefresh: () => void
}

const EndOfStayTemplateCardItem: React.FC<Props> = ({ template, onRefresh }) => {
  const { navigate } = useNavigator()

  const { toggleModal, closeModel, modalState } =
    useModalManager<'end-of-stay-template-delete-modal'>(['end-of-stay-template-delete-modal'])

  const toggleDeleteModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    toggleModal('end-of-stay-template-delete-modal')
  }

  const formulas = template.products
    .flatMap((product) => product.formula)
    .filter((formula, index, self) => {
      // Remove duplicate formulas
      return self.findIndex((f) => f.id === formula.id) === index
    })
  const countries = template.products
    .flatMap((product) => product.country)
    .filter((country, index, self) => {
      // Remove duplicate countries
      return self.findIndex((c) => c.code === country.code) === index
    })
  const options = template.options

  return (
    <>
      <div
        className="border cursor-pointer border-grey-light rounded-lg p-4 flex flex-col justify-between gap-2 w-full hover:shadow-[0px_12px_26px_0px_#93939333] transition-all duration-300"
        onClick={() => {
          navigate(
            Paths.SURVEY_END_OF_STAY_TEMPLATE_QUESTIONS_LIST_PAGE.replace(
              ':id',
              template.id.toString()
            )
          )
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex  items-center gap-2">
              <Typography
                variant="tag"
                className="cursor-text"
                onClick={(e) => e.stopPropagation()}
              >
                {template.name}
              </Typography>
              <Badge
                className="whitespace-nowrap"
                text={`Template #${template.id}`}
                variant="primary"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                icon={DeleteIcon}
                size="small"
                className="px-1.5"
                onClick={toggleDeleteModal}
              />
              <Button
                variant="secondary"
                icon={PenIcon}
                size="small"
                className="px-1.5"
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(
                    Paths.SURVEY_END_OF_STAY_TEMPLATE_UPDATE_PAGE.replace(
                      ':id',
                      template.id.toString()
                    )
                  )
                }}
              />
            </div>
          </div>
          {Array.isArray(formulas) && formulas.length > 0 && (
            <Typography
              variant="label"
              color={tailwindColors.grey.lighter}
              className="line-clamp-2 text-ellipsis mr-3"
              title={formulas.map((formula) => getTranslatedName(formula)).join(', ')}
            >
              Formule(s) : {formulas.map((formula) => getTranslatedName(formula)).join(', ')}
            </Typography>
          )}

          {Array.isArray(options) && options.length > 0 && (
            <Typography
              variant="label"
              color={tailwindColors.grey.lighter}
              className="line-clamp-2 text-ellipsis mr-3"
              title={options.map((option) => getTranslatedName(option)).join(', ')}
            >
              Option(s) : {options.map((option) => getTranslatedName(option)).join(', ')}
            </Typography>
          )}
        </div>

        <div className="flex items-center gap-2 w-full">
          <div className="flex items-center gap-3 bg-white-active rounded p-3 flex-wrap">
            {countries.map((country) => (
              <CountryFlag key={country.code} code={country.code} size="large" />
            ))}
          </div>
          <div className="flex items-center gap-3 bg-white-active rounded p-3 flex-wrap">
            <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
              {template.questionsCount} questions
            </Typography>
          </div>
        </div>
      </div>
      <EndOfJourneyTemplateDeleteModal
        template={template}
        open={modalState['end-of-stay-template-delete-modal']}
        onClose={() => closeModel('end-of-stay-template-delete-modal')}
        onSuccess={onRefresh}
      />
    </>
  )
}

export default EndOfStayTemplateCardItem
